// ---------------------------------------------------------------------------
// "Why did this lead not get a DM?"
//
// REQUIREMENT from Sales Factory, 17 August 2026: a lead who was already a
// connection gets no automatic DM (server: `helpers/dm-eligibility.ts`). That
// leaves a lead sitting in a campaign with a step that will never fire on its
// own — and a step that silently never fires is the failure this product keeps
// having. So the screen has to say it, in the place where the person can act
// on it.
//
// The decision lives here rather than inline in the page for the usual reason:
// it is asked in two places — the status chip in the table and the notice in
// the expanded row — and two copies of one condition drift. It would take one
// change to the chip for a lead to read "skipped" in the table and show no
// explanation when opened.
//
// THE THIRD INPUT IS THE ONE THAT IS EASY TO FORGET. A campaign with no DM step
// withheld nothing; calling its already-connected leads "skipped" would invent
// a decision nobody made and put a warning on a perfectly ordinary lead.
// ---------------------------------------------------------------------------

export interface DmWithheldInput {
  /** `ConnectionProjection.alreadyConnected` — connected without an invitation of ours. */
  alreadyConnected?: string;
  /** Set once a DM has actually gone out, by the scheduler or by hand. */
  dmSentAt?: string;
  /** Does this campaign's sequence even have a DM step? */
  campaignHasDmStep: boolean;
}

/**
 * Is this lead's DM being withheld right now?
 *
 * True only while all three hold: the connection is not ours, no DM has gone
 * out, and there is a DM step to withhold. Once somebody sends one by hand this
 * turns false and the lead moves on to "DM sent" — the notice is about a
 * pending decision, not a permanent property of the relationship.
 */
export function dmWithheld(input: DmWithheldInput): boolean {
  return !!input.alreadyConnected && !input.dmSentAt && input.campaignHasDmStep;
}

/**
 * What the expanded lead says about it, or null when nothing is withheld.
 *
 * One sentence for the fact and one for the way out. The way out names the
 * button by its label, because "send it manually" sends people to LinkedIn and
 * the point of the action is that the message goes out from here, recorded,
 * against this lead.
 */
export function dmWithheldNotice(input: DmWithheldInput): { text: string; action: string } | null {
  if (!dmWithheld(input)) return null;
  return {
    text: 'This lead was already connected before this campaign, so no DM was sent automatically.',
    action: 'If you still want to send it, click “Send DM now”.',
  };
}
